import { useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import type { LinkPickerPlugin } from '@atlaskit/link-picker';

import { getArticlesResolver, getPagesResolver, getRequestFormsResolver } from '../../services/csm/link-picker-resolvers';

import { LinkPickerEmptyState } from './link-picker-empty-state';
import { messages } from './messages';

export interface UseLinkPickerPluginsProps {
	linkPickerPlugins: LinkPickerPlugin[];
}

export const useLinkPickerPlugins = ({
	cloudId,
	helpCenterAri,
	baseUrl,
	helpCenterType,
}: {
	baseUrl: string;
	cloudId: string;
	helpCenterAri: string;
	helpCenterType: string;
}): UseLinkPickerPluginsProps => {
	const { formatMessage } = useIntl();

	const pagesTab = useMemo(() => {
		const pagesResolver = getPagesResolver(cloudId, helpCenterAri, baseUrl);

		return {
			tabKey: 'PAGES',
			tabTitle: formatMessage(messages.pagesTabTitle),
			resolve: pagesResolver,
			emptyStateNoResults: LinkPickerEmptyState,
		};
	}, [cloudId, helpCenterAri, baseUrl, formatMessage]);

	const articleTab = useMemo(() => {
		const articleResolver = getArticlesResolver(cloudId, helpCenterAri, baseUrl, helpCenterType);

		return {
			tabKey: 'ARTICLE',
			tabTitle: formatMessage(messages.articlesTabTitle),
			resolve: articleResolver,
			emptyStateNoResults: LinkPickerEmptyState,
		};
	}, [cloudId, helpCenterAri, baseUrl, helpCenterType, formatMessage]);

	const requestFormsTab = useMemo(() => {
		const requestFormsResolver = getRequestFormsResolver(cloudId, helpCenterAri, baseUrl);

		return {
			tabKey: 'REQUEST_FORMS',
			tabTitle: formatMessage(messages.requestFormsTabTitle),
			resolve: requestFormsResolver,
			emptyStateNoResults: LinkPickerEmptyState,
		};
	}, [cloudId, helpCenterAri, baseUrl, formatMessage]);

	return useMemo(() => {
		const linkPickerPlugins = [];
		switch (helpCenterType) {
			case 'CUSTOMER_SERVICE':
				linkPickerPlugins.push(pagesTab, articleTab);
				break;
			case 'HELP_HUB':
				linkPickerPlugins.push(pagesTab, articleTab, requestFormsTab);
				break;
		}

		return {
			linkPickerPlugins,
		};
	}, [pagesTab, articleTab, requestFormsTab, helpCenterType]);
};
